<script>
import amountMask from '@lib/utils/amount-mask'

export default {
  name: 'ProductFormShippingStep',
  props: {
    form: {
      type: Object,
      default: () => {},
    },
    salesTax: {
      type: Number,
      default: 0,
    },
    type: {
      type: String,
      required: true,
      validator: v => !v || /supplier|provider/.test(v),
    },
  },
  computed: {
    rules() {
      const { length, height, depth, unit } = this.form.shipping.dimensions
      const dimensionRequired = !!length || !!height || !!depth || !!unit
      const { value: weightVal, unit: weightUnit } = this.form.shipping.weight
      const weightRequired = !!weightVal || !!weightUnit

      return {
        'shipping.dimensions.unit': [
          {
            required: dimensionRequired,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
        'shipping.dimensions.length': [
          {
            required: dimensionRequired,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
        'shipping.dimensions.height': [
          {
            required: dimensionRequired,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
        'shipping.dimensions.depth': [
          {
            required: dimensionRequired,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
        'shipping.weight.value': [
          {
            required: weightRequired,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
        'shipping.weight.unit': [
          {
            required: weightRequired,
            message: 'This field is required',
            trigger: 'blur',
          },
        ],
      }
    },
    isTypeProvider() {
      return this.type === 'provider'
    },
  },
  methods: {
    amountMask,
    changeValue(field, value) {
      this.$emit('change', { field, value })
    },
    inputAppend(val) {
      return val ? val.toUpperCase() : '-'
    },
    calcAfterTax(field, target) {
      const priceBefore = this.form.shipping.shipping_before_tax
      const salesTaxApplicable = this.form.sales_tax_applicable
      const salesTax = this.salesTax / 100
      let value

      if (priceBefore === null) {
        value = null
      } else if (priceBefore && salesTaxApplicable === 'Applicable') {
        value = priceBefore * (1 + salesTax)
      } else if (priceBefore && (salesTaxApplicable === 'Not Applicable' || salesTaxApplicable === 'Free of Sales Tax')) {
        value = priceBefore
      }

      this.$emit('change', { field: `shipping.${target}`, value: value && Number(value).toFixed(2) })
      return value && Number(value).toFixed(2)
    },
  },
}
</script>

<template>
  <div>
    <el-form
      class="modal-form"
      label-position="top"
      :model="form"
      :rules="rules"
      :validate-on-rule-change="false"
    >
      <div class="united-field">
        <el-form-item
          label="Shipping Cost Before Tax"
        >
          <el-input
            placeholder="Enter amount"
            :value="form.shipping.shipping_before_tax"
            @input="changeValue('shipping.shipping_before_tax', amountMask($event))"
          >
            <template #prepend>
              $
            </template>
            <template
              #append
            >
              {{ form.currency }}
            </template>
          </el-input>
        </el-form-item>

        <el-form-item
          label="After Tax Price"
        >
          <el-input
            :value="isTypeProvider ? calcAfterTax('shipping_before_tax', 'shipping_after_tax') : form.shipping.shipping_after_tax"
            :placeholder="!isTypeProvider && 'Enter amount'"
            :disabled="isTypeProvider"
            @input="changeValue('shipping.shipping_after_tax', amountMask($event))"
          >
            <template #prepend>
              $
            </template>
            <template
              #append
            >
              {{ form.currency }}
            </template>
          </el-input>
        </el-form-item>
      </div>

      <span :class="$style.subtitle">Shipping Dimensions</span>

      <div class="united-field">
        <el-form-item
          label="Dimensions Unit"
          prop="shipping.dimensions.unit"
        >
          <el-select
            :value="form.shipping.dimensions.unit"
            @input="changeValue('shipping.dimensions.unit', $event)"
          >
            <el-option
              :value="null"
              label="None"
            />
            <el-option
              value="in"
              label="in - Inches"
            />
            <el-option
              value="cm"
              label="cm - Centimeters"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          label="Length"
          prop="shipping.dimensions.length"
        >
          <el-input
            type="number"
            placeholder="Enter length"
            :value="form.shipping.dimensions.length"
            @input="changeValue('shipping.dimensions.length', $event)"
          >
            <template
              #append
            >
              {{ inputAppend(form.shipping.dimensions.unit) }}
            </template>
          </el-input>
        </el-form-item>
      </div>

      <div class="united-field">
        <el-form-item
          label="Height"
          prop="shipping.dimensions.height"
        >
          <el-input
            type="number"
            placeholder="Enter height"
            :value="form.shipping.dimensions.height"
            @input="changeValue('shipping.dimensions.height', $event)"
          >
            <template
              #append
            >
              {{ inputAppend(form.shipping.dimensions.unit) }}
            </template>
          </el-input>
        </el-form-item>

        <el-form-item
          label="Depth"
          prop="shipping.dimensions.depth"
        >
          <el-input
            type="number"
            placeholder="Enter depth"
            :value="form.shipping.dimensions.depth"
            @input="changeValue('shipping.dimensions.depth', $event)"
          >
            <template
              #append
            >
              {{ inputAppend(form.shipping.dimensions.unit) }}
            </template>
          </el-input>
        </el-form-item>
      </div>

      <div class="united-field">
        <el-form-item
          label="Weight Unit"
          prop="shipping.weight.unit"
        >
          <el-select
            :value="form.shipping.weight.unit"
            @input="changeValue('shipping.weight.unit', $event)"
          >
            <el-option
              :value="null"
              label="None"
            />
            <el-option
              value="kg"
              label="kg - Kilograms"
            />
            <el-option
              value="lb"
              label="lb - Pounds"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          label="Weight"
          prop="shipping.weight.value"
        >
          <el-input
            type="number"
            placeholder="Enter weight"
            :value="form.shipping.weight.value"
            @input="changeValue('shipping.weight.value', $event)"
          >
            <template
              #append
            >
              {{ inputAppend(form.shipping.weight.unit) }}
            </template>
          </el-input>
        </el-form-item>
      </div>
    </el-form>
  </div>
</template>

<style lang="scss" module>
.subtitle {
  display: block;
  margin: 2rem 0;
  font-size: 1.1rem;
  font-weight: 700;
}
</style>
